import type { AuditEntry } from '@planuze/admin-contracts';
import {
  Button,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { Link } from '@tanstack/react-router';
import { FileText } from 'lucide-react';
import type { JSX } from 'react';
import { useAdminLocale } from '../../content/locale-provider.js';
import { useAuditTrailContent } from '../../content/sections/audit-trail.js';
import { fmtDateTime } from '../../lib/format.js';
import { HTTP } from '../../lib/http.js';
import { auditActionLabel, auditTargetTypeLabel } from './audit-action-catalog.js';

interface Props {
  readonly items: readonly AuditEntry[];
  /** Há filtros ativos — diferencia "sem resultados" de "trilha vazia" (ADR-0700/§6). */
  readonly hasFilters: boolean;
  readonly onClearFilters: () => void;
}

/* Tom do StatusBadge por faixa de status HTTP (ADR-0708): 2xx sucesso, 4xx aviso, 5xx destrutivo.
   Reusa a fonte única de códigos HTTP do `http.ts` (exceção legítima ao no-magic-numbers). */
function statusTone(status: number): 'success' | 'warning' | 'destructive' | 'neutral' {
  if (status >= HTTP.serverError) return 'destructive';
  if (status >= HTTP.badRequest) return 'warning';
  if (status >= HTTP.ok) return 'success';
  return 'neutral';
}

/* Alvo: tipo + id, com deep-link para o detalhe quando o alvo é company/user e o id é real (o
   sentinel `unknown` das ações de criação — id gerado no downstream — não é navegável). */
function TargetIdNode({ entry }: { readonly entry: AuditEntry }): JSX.Element {
  const { targetId, targetType } = entry;
  const linkClass = 'font-mono text-sm text-foreground hover:underline';
  if (targetId !== 'unknown' && targetType === 'company') {
    return (
      <Link to="/companies/$id" params={{ id: targetId }} className={linkClass}>
        {targetId}
      </Link>
    );
  }
  if (targetId !== 'unknown' && targetType === 'user') {
    return (
      <Link to="/users/$id" params={{ id: targetId }} className={linkClass}>
        {targetId}
      </Link>
    );
  }
  return <span className="font-mono text-sm text-muted-foreground">{targetId}</span>;
}

export function AuditTable({ items, hasFilters, onClearFilters }: Props) {
  const { locale } = useAdminLocale();
  const copy = useAuditTrailContent().table;
  if (items.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={<FileText className="size-6" />}
        title={copy.emptyFilteredTitle}
        description={copy.emptyFilteredDescription}
        primaryAction={
          <Button variant="outline" onClick={onClearFilters}>
            {copy.clearFilters}
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={<FileText className="size-6" />}
        title={copy.emptyTitle}
        description={copy.emptyDescription}
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{copy.columnWhen}</TableHead>
          <TableHead>{copy.columnActor}</TableHead>
          <TableHead>{copy.columnAction}</TableHead>
          <TableHead>{copy.columnTarget}</TableHead>
          <TableHead>{copy.columnDetail}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((entry) => (
          <TableRow key={entry.id}>
            <TableCell className="whitespace-nowrap text-muted-foreground">
              {fmtDateTime(entry.ts)}
            </TableCell>
            <TableCell className="text-foreground">{entry.actorEmail ?? entry.actorId}</TableCell>
            <TableCell>
              <StatusBadge variant="neutral">{auditActionLabel(entry.action, locale)}</StatusBadge>
            </TableCell>
            <TableCell>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-muted-foreground">
                  {auditTargetTypeLabel(entry.targetType, locale)}
                </span>
                <TargetIdNode entry={entry} />
              </div>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <StatusBadge variant={statusTone(entry.responseStatus)}>
                  {entry.responseStatus}
                </StatusBadge>
                {entry.reason !== undefined && (
                  <span
                    className="max-w-xs truncate text-sm text-muted-foreground"
                    title={entry.reason}
                  >
                    {entry.reason}
                  </span>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
